import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * Wraps routes that require authentication and optionally specific roles.
 * - Not authenticated → redirect to login.
 * - Authenticated but Redux user not yet hydrated from cookies → render
 *   `null` for the brief moment between the cookie-derived
 *   `isAuthenticated` flipping to true and `App.useEffect`'s
 *   `initUserFromCookies` dispatch landing in Redux. This avoids the
 *   alternative of redirecting through `/account/login` and back, which
 *   produces a visible flash on bookmarked deep links.
 * - Authenticated but roles specified and user.role not in allowed roles
 *   → redirect to dashboard.
 * - Otherwise render children.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when authorized
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated (cookie-derived)
 * @param {Object} props.user - The logged-in user object (must have .role)
 * @param {string[]} props.roles - Allowed roles (e.g. ['admin'], ['support','admin']). Empty/undefined = any authenticated user.
 */
const RoleProtectedRoute = ({ children, isAuthenticated, user, roles }) => {
    if (!isAuthenticated) {
        return <Navigate to="/account/login" replace />;
    }

    // Authenticated by cookie but Redux hasn't hydrated `user` yet — render
    // nothing (a single empty paint) rather than bouncing through
    // /account/login. App.useEffect dispatches initUserFromCookies on the
    // next tick, after which this component re-renders with `user` set.
    if (!user) {
        return null;
    }

    if (roles && roles.length > 0) {
        if (!user.role) {
            return <Navigate to="/account/login" replace />;
        }

        if (!roles.includes(user.role)) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return children;
};

export default RoleProtectedRoute;
